import { styled } from '@mui/material';
import React, { ChangeEvent } from 'react';

import { colorSystem, focusVisibleRing, h5 } from '../../theme';
import { Flexbox } from '../Flexbox';

export type FolderTabsProps<T extends string> = {
    value: T;
    onChange: (event: ChangeEvent<{}>, value: T) => void;
    children: (React.ReactElement<FolderTabProps<T>> | boolean | null | undefined)[];
    background?: string;
};

export type FolderTabProps<T extends string> = {
    value: T;
    label: React.ReactNode;
};

export function FolderTab<T extends string>(_props: FolderTabProps<T>): React.ReactElement {
    return null as never;
}

function FolderTabButton<T extends string>({
    tabValue,
    label,
    isActive,
    background,
    borderColor,
    onChange,
}: {
    tabValue: T;
    label: React.ReactNode;
    isActive: boolean;
    background: string;
    borderColor: string;
    onChange: (event: ChangeEvent<{}>, value: T) => void;
}): React.ReactElement {
    const [hovered, setHovered] = React.useState(false);
    const showHover = hovered && !isActive;

    return (
        <FolderTabRoot
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={(e) => onChange(e as unknown as ChangeEvent<{}>, tabValue)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                ...h5,
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                background: isActive ? '#ffffff' : showHover ? colorSystem.neutral[1] : background,
                color: isActive ? colorSystem.neutral[9] : showHover ? colorSystem.primary[7] : colorSystem.neutral[7],
                border: `1px solid ${isActive ? borderColor : 'transparent'}`,
                borderBottom: isActive ? '1px solid #ffffff' : `1px solid ${borderColor}`,
                borderRadius: '6px 6px 0 0',
                padding: '8px 14px',
                marginBottom: -1,
                position: 'relative',
                zIndex: isActive ? 1 : 0,
                transition: 'color 0.15s ease, background 0.15s ease',
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </FolderTabRoot>
    );
}

// The ring inside the border, as on the other tabs: the neighbouring tab would cover a ring outside it.
const FolderTabRoot = styled('button')(focusVisibleRing, { '&:focus-visible': { outlineOffset: '-2px' } });

export function FolderTabs<T extends string>({
    value,
    onChange,
    children,
    background = 'transparent',
}: FolderTabsProps<T>): React.ReactElement {
    const borderColor = colorSystem.neutral[2];

    const tabs = React.Children.map(children, (child) => {
        if (!React.isValidElement<FolderTabProps<T>>(child)) return null;
        return (
            <FolderTabButton
                key={child.props.value}
                tabValue={child.props.value}
                label={child.props.label}
                isActive={child.props.value === value}
                background={background}
                borderColor={borderColor}
                onChange={onChange}
            />
        );
    });

    return (
        <Flexbox
            role="tablist"
            style={{
                background,
                borderBottom: `1px solid ${borderColor}`,
                paddingLeft: 16,
                paddingRight: 16,
                position: 'relative',
                zIndex: 1,
            }}
            sx={{
                alignItems: 'flex-end',
            }}
        >
            {tabs}
        </Flexbox>
    );
}
